import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { notifyHost } from '../../lib/notificationDispatch';
import Sidebar, { SidebarProvider } from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';
import { format } from 'date-fns';
import ReviewRemovalsTab from './components/ReviewRemovalsTab';
import DeleteRequestsTab from './components/DeleteRequestsTab';
import ToastNotification from './components/ToastNotification';
import PendingEditsTab from './components/PendingEditsTab';

const STATUS_CONFIG = {
  pending_review: { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800 border-green-200' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800 border-red-200' },
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  unpublished: { label: 'Unpublished', color: 'bg-orange-100 text-orange-800 border-orange-200' },
};

const CATEGORY_LABELS = {
  educational: 'Educational',
  sports: 'Sports',
  arts: 'Arts',
  technology: 'Technology',
  outdoor: 'Outdoor',
  indoor: 'Indoor',
  social: 'Social',
  cultural: 'Cultural',
};

const EventManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all_events');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [pendingRemovalsCount, setPendingRemovalsCount] = useState(0);
  const [pendingDeletesCount, setPendingDeletesCount] = useState(0);
  const [pendingEditsCount, setPendingEditsCount] = useState(0);
  const [pendingEditsRefreshToken, setPendingEditsRefreshToken] = useState(0);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: sessionData, error: sessionError } = await supabase?.auth?.getSession();
      if (sessionError || !sessionData?.session) {
        setError('You must be logged in as an admin to view events.');
        setLoading(false);
        return;
      }

      let query = supabase?.from('events')?.select(`
          id, title, description, category, difficulty, image_url,
          location_name, location_address, start_date, end_date,
          registration_deadline, price, max_participants, min_age, max_age,
          requirements, what_to_bring, cancellation_policy, event_type,
          safety_rating, duration_minutes, status, is_published,
          rejection_reason, is_featured, created_at, updated_at,
          additional_images, ticket_types, recurring_pattern,
          pending_edits,
          host_id,
          event_host_profiles (
            id, organization_name, full_name, email, avatar_url,
            is_verified, host_type, verification_status
          )
        `)?.order('created_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query?.eq('status', statusFilter);
      }

      let { data, error: fetchError } = await query;

      if (fetchError) {
        console.warn('Events join query failed, retrying without host join:', fetchError?.message);
        let fallbackQuery = supabase?.from('events')?.select(`
            id, title, description, category, difficulty, image_url,
            location_name, location_address, start_date, end_date,
            registration_deadline, price, max_participants, min_age, max_age,
            requirements, what_to_bring, cancellation_policy, event_type,
            safety_rating, duration_minutes, status, is_published,
            rejection_reason, is_featured, created_at, updated_at,
            additional_images, ticket_types, recurring_pattern,
            pending_edits,
            host_id
          `)?.order('created_at', { ascending: false });

        if (statusFilter !== 'all') {
          fallbackQuery = fallbackQuery?.eq('status', statusFilter);
        }

        const fallbackResult = await fallbackQuery;
        if (fallbackResult?.error) throw fallbackResult?.error;
        data = fallbackResult?.data;
      }

      setEvents(data || []);

      const allQuery = await supabase?.from('events')?.select('status');
      if (!allQuery?.error && allQuery?.data) {
        const counts = { total: allQuery?.data?.length, pending: 0, approved: 0, rejected: 0 };
        allQuery?.data?.forEach((e) => {
          if (e?.status === 'pending_review') counts.pending++;
          if (e?.status === 'approved') counts.approved++;
          if (e?.status === 'rejected') counts.rejected++;
        });
        setStats(counts);
      }
    } catch (err) {
      console.error('fetchEvents error:', err);
      setError(err?.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchEvents();
    fetchPendingRemovalsCount();
    fetchPendingDeletesCount();
    fetchPendingEditsCount();
  }, [fetchEvents]);

  const fetchPendingRemovalsCount = async () => {
    try {
      const { count, error } = await supabase
        ?.from('review_removal_requests')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('status', 'pending');
      if (!error) setPendingRemovalsCount(count || 0);
    } catch {
      // silently fail
    }
  };

  const fetchPendingDeletesCount = async () => {
    try {
      const { count, error } = await supabase
        ?.from('event_delete_requests')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('status', 'pending');
      if (!error) setPendingDeletesCount(count || 0);
    } catch {
      // silently fail
    }
  };

  const fetchPendingEditsCount = async () => {
    try {
      const { count, error } = await supabase
        ?.from('events')
        ?.select('id', { count: 'exact', head: true })
        ?.not('pending_edits', 'is', null);
      if (!error) setPendingEditsCount(count || 0);
    } catch {
      // silently fail
    }
  };

  // Refreshes every tab's data at once — used by the manual Refresh button
  // and by the auto-refresh poll below. `pendingEditsRefreshToken` is bumped
  // so the mounted PendingEditsTab (if any) refetches too, since it's not
  // remounted just by staying on that tab.
  const refreshAll = useCallback(() => {
    fetchEvents();
    fetchPendingRemovalsCount();
    fetchPendingDeletesCount();
    fetchPendingEditsCount();
    setPendingEditsRefreshToken((t) => t + 1);
  }, [fetchEvents]);

  // Auto-refresh: periodically re-sync so a host's live edit shows up
  // without needing a manual refresh or a tab switch.
  useEffect(() => {
    const intervalId = setInterval(refreshAll, 30000);
    return () => clearInterval(intervalId);
  }, [refreshAll]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleApprove = async (event) => {
    setActionLoading(true);
    setActionError(null);
    try {
      const { error: updateError } = await supabase?.from('events')?.update({
          status: 'approved',
          is_published: true,
          rejection_reason: null,
          updated_at: new Date()?.toISOString(),
        })?.eq('id', event?.id);
      if (updateError) throw updateError;

      const host = event?.event_host_profiles;
      await notifyHost({
        userId: event?.host_id,
        eventId: event?.id,
        notificationType: 'event_approved',
        title: '🎉 Event Approved!',
        body: `"${event?.title}" has been approved and is now live.`,
        emailTemplate: 'event_approval',
        emailTo: host?.email,
        emailSubject: 'Your KidQuest Event Has Been Approved',
        emailData: {
          user_name: host?.full_name || 'there',
          event_name: event?.title || 'Your event',
          event_date: event?.start_date
            ? format(new Date(event.start_date), 'EEEE, d MMMM yyyy')
            : '',
        },
      });

      showSuccess(`"${event?.title}" has been approved and published.`);
      fetchEvents();
    } catch (err) {
      setActionError(err?.message || 'Failed to approve event');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason?.trim()) {
      setActionError('Please provide a rejection reason.');
      return;
    }
    setActionLoading(true);
    setActionError(null);
    try {
      const { error: updateError } = await supabase?.from('events')?.update({
          status: 'rejected',
          is_published: false,
          rejection_reason: rejectionReason?.trim(),
          updated_at: new Date()?.toISOString(),
        })?.eq('id', selectedEvent?.id);
      if (updateError) throw updateError;

      const host = selectedEvent?.event_host_profiles;
      await notifyHost({
        userId: selectedEvent?.host_id,
        eventId: selectedEvent?.id,
        notificationType: 'event_rejected',
        title: '❌ Event Not Approved',
        body: `"${selectedEvent?.title}" was not approved. Reason: ${rejectionReason?.trim()}`,
        emailTemplate: 'event_rejection',
        emailTo: host?.email,
        emailSubject: 'Update on Your KidQuest Event',
        emailData: {
          user_name: host?.full_name || 'there',
          event_name: selectedEvent?.title || 'Your event',
          rejection_reason: rejectionReason?.trim(),
        },
      });

      showSuccess(`"${selectedEvent?.title}" has been rejected.`);
      setRejectModalOpen(false);
      setSelectedEvent(null);
      setRejectionReason('');
      fetchEvents();
    } catch (err) {
      setActionError(err?.message || 'Failed to reject event');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleFeatured = async (event) => {
    setActionLoading(true);
    try {
      const { error: updateError } = await supabase?.from('events')?.update({ is_featured: !event?.is_featured, updated_at: new Date()?.toISOString() })?.eq('id', event?.id);
      if (updateError) throw updateError;
      showSuccess(`"${event?.title}" featured status updated.`);
      fetchEvents();
    } catch (err) {
      setActionError(err?.message || 'Failed to update featured status');
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectModal = (event) => {
    setSelectedEvent(event);
    setRejectionReason('');
    setActionError(null);
    setRejectModalOpen(true);
  };

  const filteredEvents = events?.filter((e) => {
    if (statusFilter === 'all' && e?.status === 'draft') return false;
    if (!searchQuery?.trim()) return true;
    const q = searchQuery?.toLowerCase();
    return (
      e?.title?.toLowerCase()?.includes(q) ||
      e?.description?.toLowerCase()?.includes(q) ||
      e?.location_name?.toLowerCase()?.includes(q) ||
      e?.event_host_profiles?.organization_name?.toLowerCase()?.includes(q) ||
      e?.event_host_profiles?.full_name?.toLowerCase()?.includes(q)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  return (
    <>
      <SidebarProvider>
        <div className="flex h-screen bg-background overflow-hidden">
          <Sidebar />
          <div className="flex-1 flex flex-col overflow-hidden ml-[240px]">
            <main className="flex-1 overflow-y-auto p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <Breadcrumbs items={[{ label: 'Event Management', path: '/event-management' }]} />
                  <h1 className="text-3xl font-bold text-foreground mt-2">Event Management</h1>
                  <p className="text-muted-foreground mt-1">Review and approve events submitted by hosts</p>
                </div>
                <button
                  onClick={refreshAll}
                  className="group flex items-center gap-2 px-3 py-2 text-sm font-medium bg-primary text-primary-foreground border border-primary rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <Icon name="RefreshCw" size={14} className="transition-transform duration-500 group-hover:rotate-[360deg]" />
                  Refresh
                </button>
              </div>

              {/* Tabs */}
              <div className="flex items-center gap-1 mb-6 border-b border-border">
                <button
                  onClick={() => setActiveTab('all_events')}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                    activeTab === 'all_events' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Calendar" size={15} />
                  All Events
                </button>
                <button
                  onClick={() => setActiveTab('pending_edits')}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                    activeTab === 'pending_edits' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="GitCompare" size={15} />
                  Pending Edits
                  {pendingEditsCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-amber-500 text-white rounded-full">
                      {pendingEditsCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('review_removals')}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                    activeTab === 'review_removals' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="MessageSquareX" size={15} />
                  Review Removals
                  {pendingRemovalsCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-red-500 text-white rounded-full">
                      {pendingRemovalsCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('delete_requests')}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                    activeTab === 'delete_requests' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Trash2" size={15} />
                  Delete Requests
                  {pendingDeletesCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-red-500 text-white rounded-full">
                      {pendingDeletesCount}
                    </span>
                  )}
                </button>
              </div>

              {activeTab === 'review_removals' ? (
                <ReviewRemovalsTab />
              ) : activeTab === 'delete_requests' ? (
                <DeleteRequestsTab />
              ) : activeTab === 'pending_edits' ? (
                <PendingEditsTab
                  onCountChange={setPendingEditsCount}
                  onEditsResolved={fetchEvents}
                  refreshToken={pendingEditsRefreshToken}
                />
              ) : (
                <>
                  {/* Stats Cards */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    {[
                      { label: 'Total Events', value: stats?.total, icon: 'Calendar', color: 'text-primary' },
                      { label: 'Pending Review', value: stats?.pending, icon: 'Clock', color: 'text-yellow-600' },
                      { label: 'Approved', value: stats?.approved, icon: 'CheckCircle', color: 'text-green-600' },
                      { label: 'Rejected', value: stats?.rejected, icon: 'XCircle', color: 'text-red-600' },
                    ]?.map((stat) => (
                      <div key={stat?.label} className="bg-card border border-border rounded-xl p-4">
                        <div className="flex items-center gap-3">
                          <div className={`${stat?.color}`}>
                            <Icon name={stat?.icon} size={20} />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-foreground">{stat?.value}</p>
                            <p className="text-xs text-muted-foreground">{stat?.label}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Filters */}
                  <div className="bg-card border border-border rounded-xl p-4 mb-6 flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                      <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="text"
                        placeholder="Search events, hosts, locations..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e?.target?.value)}
                        className="w-full pl-9 pr-4 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      />
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {['all', 'pending_review', 'approved', 'rejected']?.map((s) => (
                        <button
                          key={s}
                          onClick={() => setStatusFilter(s)}
                          className={`relative px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
                            statusFilter === s
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-background text-muted-foreground border-border hover:bg-muted'
                          }`}
                        >
                          {s === 'all' ? 'All' : STATUS_CONFIG?.[s]?.label || s}
                          {s === 'pending_review' && stats?.pending > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center bg-blue-500 text-white text-[10px] font-bold rounded-full px-1 leading-none shadow-sm">
                              {stats?.pending}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Events Table */}
                  <div className="bg-card border border-border rounded-xl overflow-hidden">
                    {loading ? (
                      <div className="flex items-center justify-center py-16">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          <p className="text-sm text-muted-foreground">Loading events...</p>
                        </div>
                      </div>
                    ) : error ? (
                      <div className="flex flex-col items-center justify-center py-16 gap-3">
                        <Icon name="AlertCircle" size={32} className="text-destructive" />
                        <p className="text-sm text-destructive">{error}</p>
                        <button
                          onClick={fetchEvents}
                          className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                        >
                          Retry
                        </button>
                      </div>
                    ) : filteredEvents?.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-16 gap-3">
                        <Icon name="Calendar" size={32} className="text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">No events found</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border bg-muted/30">
                              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Event</th>
                              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Host</th>
                              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Category</th>
                              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Date</th>
                              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Featured</th>
                              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {filteredEvents?.map((event) => {
                              const statusCfg = STATUS_CONFIG?.[event?.status] || { label: event?.status, color: 'bg-gray-100 text-gray-700 border-gray-200' };
                              return (
                                <tr key={event?.id} className="hover:bg-muted/20 transition-colors">
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-3">
                                      {event?.image_url ? (
                                        <img
                                          src={event?.image_url}
                                          alt={event?.title}
                                          className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                                          onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                      ) : (
                                        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                                          <Icon name="Calendar" size={16} className="text-muted-foreground" />
                                        </div>
                                      )}
                                      <div className="min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <p className="font-medium text-foreground truncate max-w-[200px]">{event?.title}</p>
                                          {event?.pending_edits && (
                                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200 flex-shrink-0">
                                              <Icon name="Clock" size={8} />
                                              Pending Edits
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-xs text-muted-foreground truncate max-w-[200px]">{event?.location_name}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <p className="text-foreground truncate max-w-[140px]">
                                      {event?.event_host_profiles?.organization_name || event?.event_host_profiles?.full_name || '—'}
                                    </p>
                                    {event?.event_host_profiles?.is_verified && (
                                      <span className="inline-flex items-center gap-1 text-xs text-green-600">
                                        <Icon name="BadgeCheck" size={10} /> Verified
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className="text-muted-foreground capitalize">
                                      {CATEGORY_LABELS?.[event?.category] || event?.category || '—'}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                                    {formatDate(event?.start_date)}
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusCfg?.color}`}>
                                      {statusCfg?.label}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3">
                                    <button
                                      onClick={() => handleToggleFeatured(event)}
                                      disabled={actionLoading || event?.status !== 'approved'}
                                      className={`p-1.5 rounded-lg transition-colors ${
                                        event?.status !== 'approved' ?'text-gray-300 bg-gray-50 cursor-not-allowed opacity-50'
                                          : event?.is_featured
                                          ? 'text-yellow-500 bg-yellow-50 hover:bg-yellow-100' :'text-muted-foreground hover:bg-muted'
                                      }`}
                                      title={event?.status !== 'approved' ? 'Only approved events can be featured' : event?.is_featured ? 'Remove from featured' : 'Mark as featured'}
                                    >
                                      <Icon name="Star" size={14} />
                                    </button>
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={() => navigate(`/event-management/${event?.id}`)}
                                        className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                        title="View details"
                                      >
                                        <Icon name="Eye" size={14} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </main>
          </div>
        </div>
      </SidebarProvider>
      {/* Reject Modal */}
      {rejectModalOpen && selectedEvent && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
          <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-xl">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">Reject Event</h3>
              <button
                onClick={() => { setRejectModalOpen(false); setActionError(null); }}
                className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                You are rejecting <span className="font-medium text-foreground">"{selectedEvent?.title}"</span>. Please provide a reason so the host can fix and resubmit.
              </p>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Rejection Reason <span className="text-destructive">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e?.target?.value)}
                  placeholder="Explain why this event is being rejected..."
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                />
              </div>
              {actionError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  <Icon name="AlertCircle" size={14} />
                  {actionError}
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => { setRejectModalOpen(false); setActionError(null); }}
                  className="flex-1 px-4 py-2.5 text-sm font-medium border border-border rounded-xl hover:bg-muted transition-colors text-foreground"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectSubmit}
                  disabled={actionLoading || !rejectionReason?.trim()}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Icon name="XCircle" size={14} />
                  )}
                  Confirm Rejection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Popup Toast Notifications */}
      <ToastNotification
        message={successMessage}
        type="success"
        onClose={() => setSuccessMessage(null)}
      />
      <ToastNotification
        message={actionError}
        type="error"
        onClose={() => setActionError(null)}
      />
    </>
  );
};

export default EventManagement;
